import React from "react";
import clsx from "clsx";

const GRID_ICON = `${process.env.PUBLIC_URL || ""}/grid_icon.png`;

const formatEnergy = (value?: number | null) => {
  if (value === null || typeof value === "undefined") return "—";
  if (value >= 1_000) return `${(value / 1000).toFixed(1)} MWh`;
  if (value >= 1) return `${value.toFixed(1)} kWh`;
  return `${(value * 1000).toFixed(0)} Wh`;
};

const statusColors: Record<string, string> = {
  green: "text-emerald-600",
  yellow: "text-amber-500",
  orange: "text-orange-500",
  red: "text-rose-500",
  gray: "text-slate-400",
};

const availabilityStatus = (availability?: number | null): keyof typeof statusColors => {
  if (availability === null || typeof availability === "undefined") return "gray";
  if (availability >= 98) return "green";
  if (availability >= 90) return "yellow";
  return "red";
};

const severityTone = (severity: number | undefined | null): { tone: keyof typeof statusColors; hex: string } => {
  if (severity === 2) return { tone: "red", hex: "#ef4444" };
  if (severity === 1) return { tone: "yellow", hex: "#fbbf24" };
  return { tone: "green", hex: "#22c55e" };
};

const energyColor = (energyReal?: number | null, energyExpected?: number | null): { ratio: number; color: string } => {
  if (!energyExpected || energyExpected <= 0) return { ratio: 0, color: "#94a3b8" }; // gray
  const ratio = Math.min(1, Math.max(0, (energyReal || 0) / energyExpected));
  if (ratio >= 0.85) return { ratio, color: "#10b981" }; // green
  if (ratio >= 0.7) return { ratio, color: "#fbbf24" }; // yellow
  return { ratio, color: "#f97316" }; // red
};

const Sparkline: React.FC<{ values?: number[] | null; colorHex?: string }> = ({ values, colorHex }) => {
  if (!values || values.length === 0) {
    return <div className="h-10 text-xs text-slate-400">No trend</div>;
  }
  const width = 120;
  const height = 40;
  const min = Math.min(...values, 0, -3);
  const max = Math.max(...values, 0, 3);
  const range = max - min || 1;
  const points = values
    .map((v, idx) => {
      const x = (idx / Math.max(1, values.length - 1)) * width;
      const y = height - ((v - min) / range) * height;
      return `${x},${y}`;
    })
    .join(" ");

  const lineColor = colorHex || "#22c55e";

  return (
    <svg width={width} height={height} className="overflow-visible">
      <line
        x1={0}
        x2={width}
        y1={height - ((0 - min) / range) * height}
        y2={height - ((0 - min) / range) * height}
        stroke="#94a3b8"
        strokeDasharray="4 4"
        strokeWidth="1"
      />
      <polyline
        fill="none"
        stroke={lineColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

const ProgressBar: React.FC<{ ratio: number; colorHex: string }> = ({ ratio, colorHex }) => {
  const clamped = Math.min(1, Math.max(0, ratio));
  return (
    <div className="flex items-center gap-2 text-xs font-semibold">
      <div className="relative h-2 w-24 overflow-hidden rounded-full bg-slate-200/70">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${clamped * 100}%`, background: colorHex }}
        />
      </div>
      <span style={{ color: colorHex }}>{Math.round(clamped * 100)}%</span>
    </div>
  );
};

export const TransformerCard: React.FC<{
  name: string;
  energyReal?: number | null;
  energyExpected?: number | null;
  prMedian?: number | null;
  availability?: number | null;
  warnCount?: number;
  critCount?: number;
  severityDay?: number | null;
  onShowInverters?: () => void;
  prLabel?: string;
}> = ({
  name,
  energyReal,
  energyExpected,
  prMedian,
  availability,
  warnCount = 0,
  critCount = 0,
  severityDay = 0,
  onShowInverters,
  prLabel = "PR",
}) => {
  const worstAvailability = availabilityStatus(availability);
  const sevInfo = severityTone(severityDay || 0);
  const ratio = energyExpected ? Math.min(1, Math.max(0, (energyReal || 0) / energyExpected)) : 0;

  return (
    <div
      className={clsx(
        "flex h-full flex-col rounded-xl border bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
        severityDay === 2
          ? "border-rose-200"
          : severityDay === 1
          ? "border-amber-200"
          : "border-emerald-200"
      )}
    >
      <div className="h-2 w-full rounded-md" style={{ backgroundColor: sevInfo.hex }} />
      <div className="mt-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <img src={GRID_ICON} alt="" className="h-5 w-5" />
          <div className="text-sm font-semibold text-slate-800">{name}</div>
        </div>
      </div>

      <div className="mt-2 space-y-2">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Energy</span>
          <span className="font-semibold text-slate-800">{formatEnergy(energyReal)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-slate-500">Expected {formatEnergy(energyExpected)}</div>
          <ProgressBar ratio={ratio} colorHex="#10b981" />
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
          <div>
            <div className="text-slate-500">{prLabel}</div>
            <div className="font-semibold text-slate-800">
              {prMedian !== null && typeof prMedian !== "undefined" ? prMedian.toFixed(2) : "—"}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Availability</div>
            <div className={clsx("font-semibold", statusColors[worstAvailability] || "text-slate-800")}>
              {availability !== null && typeof availability !== "undefined" ? `${availability.toFixed(1)}%` : "—"}
            </div>
          </div>
          <div className="col-span-2 flex items-center gap-3 text-sm font-semibold">
            <div
              className={clsx(
                "rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                severityDay === 2
                  ? "border-rose-200 bg-rose-50 text-rose-700"
                  : severityDay === 1
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              )}
            >
              {severityDay === 2 ? "CRIT" : severityDay === 1 ? "WARN" : "NORMAL"}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="flex items-center gap-1 text-amber-600">WARN {warnCount || 0}</span>
              <span className="flex items-center gap-1 text-rose-600">CRIT {critCount || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onShowInverters}
        className="mt-auto text-xs font-semibold text-blue-700 hover:text-blue-900"
      >
        Show inverters
      </button>
    </div>
  );
};

export const InverterCard: React.FC<{
  name: string;
  energyReal?: number | null;
  energyExpected?: number | null;
  prMedian?: number | null;
  zLiveMedian?: number | null;
  zTodaySeries?: number[] | null;
  trendSeries?: { x: any; y: number }[] | null;
  onShowTrend?: () => void;
  startupStatus?: number | null;
  hasValidPrToday?: boolean;
  severityDay?: number | null;
  prLabel?: string;
  diagnosis?: any;
  hasOpenTicket?: boolean;
  availability?: number | null;
}> = ({
  name,
  energyReal,
  energyExpected,
  prMedian,
  zLiveMedian,
  zTodaySeries,
  trendSeries,
  onShowTrend,
  startupStatus = 0,
  hasValidPrToday = true,
  severityDay = 0,
  prLabel = "PR",
  diagnosis,
  hasOpenTicket = false,
  availability = null,
}) => {
  const [showDiag, setShowDiag] = React.useState(false);
  const { ratio, color } = energyColor(energyReal, energyExpected);
  const sevInfo = severityTone(severityDay || 0);
  const disabled = !hasValidPrToday;
  const badge =
    severityDay === 2 ? "CRIT" : severityDay === 1 ? "WARN" : hasValidPrToday ? "NORMAL" : "NO DATA";
  const badgeClass =
    !hasValidPrToday
      ? "bg-slate-100 text-slate-600 border-slate-200"
      : severityDay === 2
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : severityDay === 1
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";

  const startupLabel =
    startupStatus === 3 ? "Startup failure" : startupStatus === 2 ? "Delayed startup" : null;
  const startupClass =
    startupStatus === 3
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div
      className={clsx(
        "relative flex h-full flex-col rounded-xl border bg-white p-3 shadow-sm transition",
        severityDay === 2
          ? "border-rose-200"
          : severityDay === 1
          ? "border-amber-200"
          : "border-emerald-200"
      )}
    >
      {hasOpenTicket && (
        <span className="absolute right-2 top-2 h-3 w-3 rounded-full bg-rose-500" title="Open ticket" />
      )}
      <div className="h-2 w-full rounded-md" style={{ backgroundColor: sevInfo.hex }} />
      <div className="mt-2 flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">{name}</div>
        <div className={clsx("rounded-full border px-2 py-0.5 text-[11px] font-semibold", badgeClass)}>
          {badge}
        </div>
      </div>
      {startupLabel && (
        <div
          className={clsx(
            "mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
            startupClass
          )}
        >
          {startupLabel}
        </div>
      )}
      {!hasValidPrToday && <div className="mt-1 text-xs text-slate-500">No valid data today</div>}
      <div className="mt-2 space-y-2 text-xs text-slate-600">
        <div className="flex items-center justify-between">
          <span>Energy</span>
          <span className="font-semibold text-slate-800">{formatEnergy(energyReal)}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-slate-500">Expected {formatEnergy(energyExpected)}</div>
          <ProgressBar ratio={ratio} colorHex={color} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-slate-500">{prLabel}</div>
            <div className={clsx("font-semibold", disabled ? "text-slate-400" : "text-slate-800")}>
              {prMedian !== null && typeof prMedian !== "undefined" ? prMedian.toFixed(2) : "—"}
            </div>
          </div>
          <div>
            <div className="text-slate-500">Performance Deviation</div>
            <div className={clsx("font-semibold", disabled ? "text-slate-400" : "text-slate-800")}>
              {zLiveMedian !== null && typeof zLiveMedian !== "undefined" ? zLiveMedian.toFixed(2) : "—"}
            </div>
          </div>
          <div className="text-xs text-slate-600">
            <div className="text-slate-500">Today trend</div>
            {!disabled ? (
              <Sparkline values={zTodaySeries || []} colorHex={sevInfo.hex} />
            ) : (
              <div className="h-10 text-xs text-slate-400">No trend</div>
            )}
          </div>
          <div className="flex flex-col items-end justify-center text-xs">
            <div className="text-slate-500">Availability</div>
            <div className={clsx("font-semibold", statusColors[availabilityStatus(availability)])}>
              {availability !== null && typeof availability !== "undefined" ? `${availability.toFixed(1)}%` : "—"}
            </div>
          </div>
        </div>
        {diagnosis && (
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <button
              type="button"
              onClick={() => setShowDiag((v) => !v)}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
              title="View diagnostic details"
            >
              !
            </button>
            <span className="truncate" title={diagnosis.message || "Diagnostic details"}>
              {diagnosis.title || "Diagnostic"}
            </span>
          </div>
        )}
      </div>

      {showDiag && diagnosis && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-900/10 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">{diagnosis.title || "Diagnostic"}</div>
              <button
                type="button"
                onClick={() => setShowDiag(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                x
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap">{diagnosis.message || ""}</div>
            {Array.isArray(diagnosis.suggested_checks) && diagnosis.suggested_checks.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] font-semibold text-slate-600">Suggested checks</div>
                <ul className="mt-1 list-disc pl-4 text-xs text-slate-700">
                  {diagnosis.suggested_checks.map((c: string, idx: number) => (
                    <li key={idx}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {diagnosis.metrics && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-slate-600">
                {Object.entries(diagnosis.metrics).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-semibold text-slate-800">{v === null || typeof v === "undefined" ? "—" : String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
